import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { BarcodeReader, StrichSDK } from "@pixelverse/strichjs-sdk";
import { createBarcodeReaderConfig, sdkLicenseKey } from "../../scanner-config";
import { toast } from "react-toastify";
import { LogErrors } from "../../utils/LogErrors";
import { useUserContext } from "../../context/userContext";

const BarcodeReaderComponent = forwardRef(function BarcodeReaderComponent(
  { onDetected, pageSetIsInitialised },
  ref
) {
  // StrichSDK._debug = true;
  const hostElemRef = useRef(null);
  const barcodeReaderRef = useRef(null);
  const [sdkInitialized, setSdkInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user, logout, updateTokens } = useUserContext();

  useLayoutEffect(() => {
    const initializeSDK = async () => {

      if (StrichSDK.isInitialized()) {
        setSdkInitialized(true);
        setLoading(false);
        return;
      }

      try {
        await StrichSDK.initialize(sdkLicenseKey());
        setSdkInitialized(true);
      } catch (e) {
        LogErrors(user, logout, updateTokens, `Failed to initialize STRICH SDK: ${e}`);
        handleSdkError(e);
      } finally {
        setLoading(false);
      }
    };

    initializeSDK();

    return () => {
      // LogErrors(user, logout, updateTokens, "Unmounting: Stopping barcode reader and releasing camera.");

      if (barcodeReaderRef.current) {
        barcodeReaderRef.current
          .stop()
          .then(() => barcodeReaderRef.current.destroy())
          .then(() => {
            barcodeReaderRef.current = null;
            // LogErrors(user, logout, updateTokens, "Barcode reader successfully stopped and destroyed.");
          })
          .catch((error) => LogErrors(user, logout, updateTokens, "Error stopping barcode reader: " + error));
      }
    };
  }, []);

  useEffect(() => {
    if (!sdkInitialized || !hostElemRef.current) return;

    const initBarcodeReader = async () => {
      try {
        // LogErrors(user, logout, updateTokens, "Initializing barcode reader...");
        const config = createBarcodeReaderConfig(hostElemRef.current);
        barcodeReaderRef.current = new BarcodeReader(config);
        await barcodeReaderRef.current.initialize();
        barcodeReaderRef.current.detected = onDetected;
        await barcodeReaderRef.current.start();
        pageSetIsInitialised(true);
            } catch (error) {
        LogErrors(user, logout, updateTokens, "Barcode reader initialization failed: " + error);
        handleSdkError(error);
      }
    };

    initBarcodeReader();

    return () => {
      if (barcodeReaderRef.current) {
        barcodeReaderRef.current
          .stop()
          .then(() => barcodeReaderRef.current.destroy())
          .then(() => {
            barcodeReaderRef.current = null;
            // LogErrors(user, logout, updateTokens, "Barcode reader cleanup complete.");
          })
          .catch((error) => LogErrors(user, logout, updateTokens, "Error cleaning up barcode reader: " + error));
      }
    };
  }, [sdkInitialized]);

  useImperativeHandle(ref, () => ({
    start: async () => {
      if (barcodeReaderRef.current) await barcodeReaderRef.current.start();
    },
    stop: async () => {
      if (barcodeReaderRef.current) await barcodeReaderRef.current.stop();
    },
    destroy: async () => {
      if (barcodeReaderRef.current) {
        await barcodeReaderRef.current.stop().catch(() => {});
        await barcodeReaderRef.current.destroy().catch(() => {});
        barcodeReaderRef.current = null;
      }
    },
  }));

  const handleSdkError = (error) => {
    toast.error(error.message);
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-full gap-4">
        <p>Loading scanner, please wait...</p>
        <div className="loading loading-spinner loading-lg">Loading...</div>
      </div>
    );
  }
  if (!sdkInitialized) return <div>Failed to initialize scanner.</div>;

  return <div className="barcode-scanner" ref={hostElemRef} />;
});

BarcodeReaderComponent.propTypes = {
  onDetected: PropTypes.func.isRequired,
  pageSetIsInitialised: PropTypes.func,
};

export default BarcodeReaderComponent;
